import React, { useEffect, useState } from 'react';
import { subscriptionApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { CreditCardIcon, CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const Billing: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const response = await subscriptionApi.getSubscription();
      setSubscription(response.data.subscription);
      setInvoices(response.data.subscription?.invoices || []);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await subscriptionApi.getPortalSession();
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to get portal session:', error);
    }
  };

  const getUsagePercentage = (used: number, total: number) => {
    if (total === 0) return 0;
    return Math.min((used / total) * 100, 100);
  };

  const isFreePlan = user?.plan === 'FREE';

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="mt-1 text-gray-600">Manage your subscription and billing information</p>
      </div>

      {/* Current Plan */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Current Plan</h2>
            <p className="mt-1 text-3xl font-bold text-brand-600">{user?.plan}</p>
            {subscription?.status && (
              <p className="mt-1 text-sm text-gray-500">
                Status: <span className="capitalize">{subscription.status.toLowerCase()}</span>
              </p>
            )}
          </div>
          <div className="flex gap-3">
            {!isFreePlan && (
              <button onClick={handleManageBilling} className="btn-secondary flex items-center gap-2">
                <CreditCardIcon className="h-5 w-5" />
                Manage Billing
              </button>
            )}
            {isFreePlan && (
              <Link to="/pricing" className="btn-primary flex items-center gap-2">
                <SparklesIcon className="h-5 w-5" />
                Upgrade Now
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Upgrade Prompt for Free Users */}
      {isFreePlan && (
        <div className="bg-gradient-to-r from-brand-600 to-brand-500 rounded-lg p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <SparklesIcon className="h-6 w-6" />
                Unlock Pro Features
              </h2>
              <p className="mt-2 text-brand-100">
                Upgrade to Pro and get access to AI-powered tools, unlimited opportunities, and more.
              </p>
              <ul className="mt-4 space-y-2">
                {[
                  'AI Proposal Writer',
                  'GovCon AI Assistant',
                  'CRM & Pipeline Management',
                  'Document Hub with AI Analysis',
                  'GSA & SeaPort Integration',
                  '2,000 AI Credits / month',
                  '100 GB Storage',
                ].map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm">
                    <CheckIcon className="h-4 w-4 text-brand-200" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">$375</p>
              <p className="text-brand-100">/month</p>
              <Link
                to="/pricing"
                className="mt-4 inline-block bg-white text-brand-600 px-6 py-2 rounded-lg font-semibold hover:bg-brand-50 transition"
              >
                View Plans
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Usage */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-6">Usage</h2>
        <div className="space-y-6">
          {/* AI Credits */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">AI Credits</span>
              <span className="text-gray-900 font-medium">
                {user?.aiCreditsUsed} / {user?.aiCreditsTotal === 0 ? 'Unlimited' : user?.aiCreditsTotal}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-600 h-2 rounded-full"
                style={{
                  width: `${getUsagePercentage(user?.aiCreditsUsed || 0, user?.aiCreditsTotal || 1)}%`,
                }}
              ></div>
            </div>
          </div>

          {/* Storage */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Storage</span>
              <span className="text-gray-900 font-medium">
                {((user?.storageUsed || 0) / (1024 * 1024 * 1024)).toFixed(2)} GB /{' '}
                {user?.storageLimit === 0 ? 'Unlimited' : `${((user?.storageLimit || 0) / (1024 * 1024 * 1024)).toFixed(0)} GB`}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{
                  width: `${getUsagePercentage(user?.storageUsed || 0, user?.storageLimit || 1)}%`,
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Invoice History</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {invoices.length > 0 ? (
            invoices.map((invoice: any) => (
              <div key={invoice.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(invoice.periodStart).toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                  <p className="text-xs text-gray-500">{invoice.status}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-900">
                    ${invoice.amount.toFixed(2)}
                  </span>
                  {invoice.pdfUrl && (
                    <a
                      href={invoice.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 hover:text-brand-500 text-sm"
                    >
                      Download
                    </a>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center text-gray-500">
              No invoices yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing;
